import styled from '@emotion/styled';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { JSX } from 'react';
import { COLOR, GREYSCALE } from '../styles/colors';
import { CUSTOM_ICON_DATA } from '../styles/icons';
import { SPACING } from '../styles/spacing';
import { TYPOGRAPHY } from '../styles/typography';
import carrierKeyToIconUrl from '../utils/carrierKeyToIconUrl';

export type CustomIconName = (typeof CUSTOM_ICON_DATA)[number]['iconName'];

export interface IconProps extends Omit<FontAwesomeIconProps, 'icon'> {
  icon: CustomIconName | IconProp;
}

function Icon(props: IconProps): JSX.Element {
  const { icon } = props;
  return <FontAwesomeIcon {...props} icon={icon as IconProp} />;
}

const Styled = {
  CarrierIcon: styled.img<{ carrierKey: string; scale: number }>`
    height: ${({ carrierKey, scale }) => {
      switch (carrierKey) {
        case 'UPS':
          return `${22 * scale}px`;
        case 'USPS':
          return `${17 * scale}px`;
        default:
          return `${17 * scale}px`;
      }
    }};
  `,
  TooltipIcon: styled(Icon)`
    min-width: ${TYPOGRAPHY.fontSize.sm};
    height: ${TYPOGRAPHY.fontSize.sm};
    margin: 0 ${SPACING.sm};
    font-size: ${TYPOGRAPHY.fontSize.xs};
    color: ${GREYSCALE.grey50};
    vertical-align: -0.15em;
    cursor: pointer;
    &:hover {
      color: ${COLOR.darkBlue};
    }
  `,
};

export type CarrierIconProps = {
  carrierKey: string;
  mailClassKey?: string | null;
  scale?: number;
};

export function CarrierIcon({ carrierKey, mailClassKey, scale }: CarrierIconProps): JSX.Element {
  return (
    <Styled.CarrierIcon
      src={carrierKeyToIconUrl(carrierKey, mailClassKey)}
      carrierKey={carrierKey}
      data-testid={carrierKey}
      scale={scale ?? 1}
    />
  );
}

export type TooltipIconProps = {
  id?: string;
  title?: string;
};

export function TooltipIcon({ id, title }: TooltipIconProps) {
  return (
    <Styled.TooltipIcon
      id={id}
      aria-label={`Find out more${title ? ` about ${title}` : ''}.`}
      title={title ?? ''}
      icon="circle-question"
    />
  );
}

export default Icon;
